import type { Coordinates, DesignSite } from '@freedom/shared';
import { destinationPoint, haversineDistanceM, initialBearingDeg } from '@freedom/geospatial';

export type RadioLinkKind = 'ptp' | 'ptmp';
export type ProductSelectionMode = 'auto' | 'manual';
export type MapStyleId = 'buildings' | 'terrain' | 'satellite' | 'hybrid';

export interface RadioLinkModel {
  id: string;
  name: string;
  kind: RadioLinkKind;
  startSiteId: string;
  endSiteId: string;
  groupId: string | null;
  stationNumber: number | null;
  productSelectionMode: ProductSelectionMode;
  additionalLossDb: number;
}

export const MAX_EXPLICIT_LINK_LOSS_DB = 60;

export function normalizeExplicitLinkLossDb(value: unknown, fallback = 0): number {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? Math.max(0, Math.min(MAX_EXPLICIT_LINK_LOSS_DB, numeric)) : fallback;
}

export interface PtMpGroupModel {
  id: string;
  name: string;
  apSiteId: string;
  stationSiteIds: string[];
  selectedStationId: string | null;
  productSelectionMode: ProductSelectionMode;
}

export interface DisplaySettings {
  showCoverage: boolean;
  showAllAntennas: boolean;
  showWeakCoverage: boolean;
  defaultStationHeightM: number;
  coverageAccuracy: number;
  coverageOpacity: number;
  lidarEnabled: boolean;
  lidarCoverage: boolean;
  lidarOutlierPercent: number;
  showAllStations: boolean;
  rainfallMap: boolean;
  weatherRadars: boolean;
  units: 'metric' | 'imperial';
  defaultProductSelection: ProductSelectionMode;
  mapStyle: MapStyleId;
}

export const defaultDisplaySettings: DisplaySettings = {
  showCoverage: true,
  showAllAntennas: false,
  showWeakCoverage: true,
  defaultStationHeightM: 12,
  coverageAccuracy: 65,
  coverageOpacity: 68,
  lidarEnabled: true,
  lidarCoverage: false,
  lidarOutlierPercent: 0.5,
  showAllStations: true,
  rainfallMap: false,
  weatherRadars: false,
  units: 'metric',
  defaultProductSelection: 'auto',
  mapStyle: 'hybrid',
};

export interface ProfileEndpointUpdate {
  siteId: string;
  coordinates?: Coordinates;
  antennaHeightM?: number;
}

export function radioLinkEndpoints(link: RadioLinkModel | null, sites: DesignSite[]): [DesignSite, DesignSite] | null {
  if (!link) return null;
  const start = sites.find((site) => site.id === link.startSiteId);
  const end = sites.find((site) => site.id === link.endSiteId);
  return start && end ? [start, end] : null;
}

export function moveEndpointAlongBearing(fixedSite: DesignSite, movingSite: DesignSite, distanceM: number): Coordinates {
  const bearing = initialBearingDeg(fixedSite.coordinates, movingSite.coordinates);
  return destinationPoint(fixedSite.coordinates, bearing, Math.max(10, distanceM));
}

export function averageGroupDistanceM(group: PtMpGroupModel, sites: DesignSite[]): number {
  const ap = sites.find((site) => site.id === group.apSiteId);
  if (!ap) return 0;
  const distances = group.stationSiteIds.flatMap((id) => {
    const station = sites.find((site) => site.id === id);
    return station ? [haversineDistanceM(ap.coordinates, station.coordinates)] : [];
  });
  return distances.length > 0 ? distances.reduce((sum, value) => sum + value, 0) / distances.length : 0;
}

export function formatDistance(distanceM: number, units: DisplaySettings['units']): string {
  if (units === 'imperial') {
    const feet = distanceM * 3.28084;
    return feet >= 5_280 ? `${(feet / 5_280).toFixed(2)} mi` : `${Math.round(feet)} ft`;
  }
  return distanceM >= 1_000 ? `${(distanceM / 1_000).toFixed(2)} км` : `${distanceM.toFixed(distanceM < 100 ? 1 : 0)} м`;
}
